import { useEffect } from "react";
import {
  trackScroll50,
  trackCtaClick,
  trackWhatsappIntent,
} from "../../utils/gtag";

/**
 * Side-effects-only component — mounts inside <CarService /> to register:
 *
 *   • scroll_50          — once per session, fires when user scrolls past
 *                          50% of document height. IntersectionObserver
 *                          on a midpoint sentinel for native perf (no
 *                          scroll listener).
 *   • cta_click          — global click delegate. Resolves cta_label +
 *                          cta_location from the closest element with
 *                          [data-cta-label]/[data-cta-location] markers,
 *                          falling back to button text and nearest
 *                          [data-cta-location] ancestor.
 *   • whatsapp_intent    — anchor href matches wa.me / api.whatsapp.com.
 *                          Fires even if a per-component handler is
 *                          ALSO present (so we never lose the signal).
 *
 * GA4 Enhanced Measurement's built-in scroll@90% stays enabled — this
 * 50% event is additive, not a replacement.
 */
const CarServiceFunnelTracker = () => {
  // ---- scroll_50 -----------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    // A 1px-tall sentinel placed at 50% of document height. Re-measured
    // on resize/content-changes via IntersectionObserver's threshold.
    const sentinel = document.createElement("div");
    sentinel.setAttribute("data-wd-scroll-50-sentinel", "");
    sentinel.style.cssText =
      "position:absolute;width:1px;height:1px;pointer-events:none;opacity:0;";
    document.body.appendChild(sentinel);

    const place = () => {
      const h = document.documentElement.scrollHeight || document.body.scrollHeight;
      sentinel.style.top = `${Math.round(h * 0.5)}px`;
    };
    place();

    // Re-place when DOM grows (lazy-loaded sections, image loads, etc.)
    const ro = new ResizeObserver(place);
    ro.observe(document.body);

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          trackScroll50();
          // Disconnect immediately — once-per-session anyway, no need to
          // keep listening (and sessionStorage flag handles same-session
          // navigation safely).
          io.disconnect();
          ro.disconnect();
          sentinel.remove();
        }
      },
      { rootMargin: "0px", threshold: 0 },
    );
    io.observe(sentinel);

    return () => {
      io.disconnect();
      ro.disconnect();
      if (sentinel.isConnected) sentinel.remove();
    };
  }, []);

  // ---- cta_click + whatsapp_intent delegate --------------------------
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const isWhatsappHref = (href) =>
      !!href && (href.includes("wa.me/") || href.includes("api.whatsapp.com"));

    const onClick = (e) => {
      const el = e.target && e.target.closest && e.target.closest("a, button");
      if (!el) return;

      // ---- whatsapp_intent --------------------------------------------
      const href = el.getAttribute && el.getAttribute("href");
      if (el.tagName === "A" && isWhatsappHref(href)) {
        const locEl = el.closest("[data-cta-location]");
        const location = (locEl && locEl.getAttribute("data-cta-location"))
          || el.getAttribute("data-cta-location")
          || inferWhatsappLocation(el);
        trackWhatsappIntent({ location });
        // do NOT return — also count as cta_click for the click funnel
      }

      // ---- cta_click ---------------------------------------------------
      // Trigger if author opted-in via data-cta-label OR the element is a
      // primary button-like CTA (button or link with class hints/role).
      const dataLabel = el.getAttribute("data-cta-label");
      const dataLoc   = el.getAttribute("data-cta-location");
      if (dataLabel || dataLoc) {
        trackCtaClick({
          label: dataLabel || textLabel(el) || "unknown",
          location: dataLoc
            || (el.closest("[data-cta-location]") || {}).getAttribute?.("data-cta-location")
            || "unknown",
        });
        return;
      }

      // Fallback: anchor to wa.me/tel: counts as a CTA too (already fired
      // whatsapp_intent above; for tel: it's the call-us intent).
      if (el.tagName === "A") {
        if (isWhatsappHref(href)) {
          trackCtaClick({
            label: textLabel(el) || "WhatsApp",
            location: inferWhatsappLocation(el),
          });
        } else if (href && href.startsWith("tel:")) {
          trackCtaClick({
            label: textLabel(el) || "Call Us",
            location: (el.closest("[data-cta-location]") || {}).getAttribute?.("data-cta-location")
              || "header",
          });
        }
      }
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
};

/* Walk up the DOM looking for known section markers — cheap heuristic
 * for components that haven't opted-in to data-cta-location. */
function inferWhatsappLocation(el) {
  const dt = el.closest("[data-testid]");
  const id = dt && dt.getAttribute("data-testid");
  if (!id) return "whatsapp_float";
  if (id.includes("fab") || id.includes("float")) return "whatsapp_float";
  if (id.includes("sticky")) return "sticky_bar";
  if (id.includes("header")) return "header";
  if (id.includes("footer")) return "footer";
  if (id.includes("modal")) return "modal";
  return "whatsapp_float";
}

/* Extract a clean human-readable label from a button/link. Falls back to
 * aria-label when the visible text is empty (icon-only buttons). */
function textLabel(el) {
  const raw = (el.innerText || el.textContent || "").trim();
  if (raw) return raw.replace(/\s+/g, " ").slice(0, 60);
  return el.getAttribute("aria-label") || "";
}

export default CarServiceFunnelTracker;
